'use client';

import { CredentialType } from '#/components/ethereum/credential-type';
import { FormMessage } from '#/components/form';
import { Timestamp } from '#/components/timestamp';
import { Button } from '#/components/ui/button';
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog';
import { Form, FormControl, FormField, FormItem } from '#/components/ui/form';
import { Label } from '#/components/ui/label';
import { RadioGroup, RadioGroupItem } from '#/components/ui/radio-group';
import { BeaconStatus } from '#/constants/pectra/beacon-status';
import { CredentialTypeEnum } from '#/constants/pectra/credential-type';
import { ValidatorStatusEnum } from '#/constants/validator-status';
import { EthereumStakingValidatorInfo } from '#/data/react-query/queries/order-list';
import { cn } from '#/lib/utils';
import { OrderDisplayName } from '#/modules/display/order-display-name';
import { ValidatorStatus } from '#/modules/display/validator-status';
import { zodResolver } from '@hookform/resolvers/zod';
import { useActor } from '@xstate/react';
import { Translation } from 'intl-lib';
import { useCallback, useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { MachineContext } from '../contexts/machine';

export function DestinationValidator() {
  const interpreter = useContext(MachineContext);
  const [state, dispatch] = useActor(interpreter);
  const initalTarget = state.context.target;
  const originalRow = state.context.originalRow;
  const formSchema = useMemo(
    () =>
      z.object({
        target: z
          .custom<EthereumStakingValidatorInfo | null>(
            val => val != null,
            'Please select destination validator'
          )
          .refine(
            validator => validator?.credentialType == CredentialTypeEnum.TYPE2,
            'destination_must_be_compounding'
          ),
      }),
    []
  );
  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      target: initalTarget,
    },
  });

  // 2. Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    console.log('onSubmit', values);
    const { target } = values;
    dispatch({
      type: 'NEXT',
      data: { target },
    });
  }
  const isConsolidatable = useCallback(
    (validator: EthereumStakingValidatorInfo) => {
      return (
        validator.status === ValidatorStatusEnum.ACTIVE && // isActive
        validator.exit != 1 && // not isExiting
        validator.beaconStatus === BeaconStatus.ACTIVE_ONGOING // isActiveOngoing
      );
    },
    []
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <Translation path="my_staking.consolidate_dialog.SelectDestinationValidator.Title" />
        </DialogTitle>
        <DialogDescription>
          <Translation path="my_staking.consolidate_dialog.SelectDestinationValidator.Description" />
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-2">
            <Label>
              <Translation path="my_staking.consolidate_dialog.DestinationValidator" />
            </Label>
            <div className="flex flex-col gap-3 p-3 border rounded-xl">
              <div className="flex items-center justify-between">
                <Label className="text-muted-foreground">
                  <OrderDisplayName item={originalRow} />
                </Label>
                <div className="text-sm text-muted-foreground">
                  <Timestamp date={originalRow.createTime} />
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="target"
                  render={({ field }) => (
                    <FormItem>
                      <RadioGroup
                        value={field.value?.validatorIndex}
                        onValueChange={validatorIndex =>
                          field.onChange(
                            originalRow.validators?.find(
                              val => val.validatorIndex == validatorIndex
                            )
                          )
                        }
                        className="flex flex-col gap-1"
                      >
                        <div className="grid grid-cols-4 items-center gap-2 text-xs text-muted-foreground">
                          <div className="flex items-center gap-2 pl-8">
                            <Translation path="my_staking.order.validator_column.validator" />
                          </div>
                          <div className="text-right">
                            <Translation path="my_staking.order.validator_column.credential_type" />
                          </div>
                          <div className="text-right">
                            <Translation path="my_staking.order.validator_column.balance" />
                          </div>
                          <div className="text-right">
                            <Translation path="my_staking.order.validator_column.status" />
                          </div>
                        </div>
                        {originalRow.validators?.map(validator => {
                          const isConsolidatable_ = isConsolidatable(validator);
                          return (
                            <FormItem key={validator.validatorId}>
                              <Label
                                className={cn(
                                  'h-9 px-2 grid grid-cols-4 space-y-0 gap-2 text-sm items-center rounded-md hover:bg-muted/50',
                                  {
                                    'cursor-not-allowed': !isConsolidatable_,
                                  }
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  <FormControl>
                                    <RadioGroupItem
                                      value={validator.validatorIndex!}
                                      disabled={!isConsolidatable_}
                                    />
                                  </FormControl>
                                  #{validator.validatorIndex}
                                </div>
                                <div className="text-right">
                                  <CredentialType
                                    credentialType={validator.credentialType}
                                  />
                                </div>
                                <div className="text-right">
                                  {validator.effectiveBalance} ETH
                                </div>
                                <div className="text-right">
                                  <ValidatorStatus item={validator} />
                                </div>
                              </Label>
                            </FormItem>
                          );
                        })}
                      </RadioGroup>
                      <FormMessage
                        errors={{
                          destination_must_be_compounding: (
                            <Translation path="my_staking.consolidate_dialog.error_message.destination_must_be_compounding" />
                          ),
                        }}
                      />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
        </form>
      </Form>
      {/* <div className="flex flex-col gap-3 w-full">
        <div className="font-bold">{translation.DestinationValidator}</div>
        <dl className="flex flex-col gap-4 p-5 rounded-xl border">
          <div className="flex items-center justify-between">
            <dt className="text-sm">{translation.Validator}#</dt>
            <dd className="text-sm">#{target.validatorIndex}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-sm">{translation.CredentialType}</dt>
            <dd className="text-sm">
              <CredentialType credentialType={target.credentialType} />
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-sm">{translation.ETHStaked}</dt>
            <dd className="text-sm">{target.effectiveBalance} ETH</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-sm">{translation.WithdrawalAddress}</dt>
            <dd className="text-sm">
              <EthereumAccount address={target.withdrawaddr} />
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-sm">{translation.ClaimAddress}</dt>
            <dd className="text-sm">
              <EthereumAccount address={target.claimaddr} />
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-sm">{translation.Created}</dt>
            <dd className="text-sm">
              <Timestamp date={target.activation} />
            </dd>
          </div>
        </dl>
      </div> */}
      <DialogFooter className="gap-2 sm:space-x-0">
        <DialogClose asChild>
          <Button variant="secondary" type="button">
            <Translation path="my_staking.Validators.Dialog.CancelButtonLabel" />
          </Button>
        </DialogClose>
        <Button
          type="button"
          variant="primary"
          onClick={form.handleSubmit(onSubmit)}
        >
          <Translation path="my_staking.Validators.Dialog.NextButtonLabel" />
        </Button>
      </DialogFooter>
    </>
  );
}
